<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import PurchaseProjectStatusBadge from './PurchaseProjectStatusBadge.vue';
import PurchaseProjectSummary from './PurchaseProjectSummary.vue';
import PurchaseProjectItems from './PurchaseProjectItems.vue';
import PurchaseProjectActivity from './PurchaseProjectActivity.vue';
import ProductSearchDrawer from './ProductSearchDrawer.vue';
import EditPurchaseProjectDrawer from './EditPurchaseProjectDrawer.vue';
import LinkedTasksPreview from 'dashboard/components-next/Tasks/LinkedTasksPreview.vue';
import { mockProducts } from './story/fixtures.js';

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  contactId: {
    type: [String, Number],
    default: null,
  },
  contactName: {
    type: String,
    default: '',
  },
  contactPhone: {
    type: String,
    default: '',
  },
  contactEmail: {
    type: String,
    default: '',
  },
  contactPreferredChannel: {
    type: String,
    default: 'whatsapp',
  },
});

const emit = defineEmits(['back', 'updated']);

const { t } = useI18n();

const local = reactive({
  title: props.project.title ?? '',
  sellerId: props.project.sellerId ?? '',
  sellerName: props.project.sellerName ?? '',
  status: props.project.status ?? 'draft',
  occasion: props.project.occasion ?? '',
  dueDate: props.project.dueDate ?? '',
  internalNote: props.project.internalNote ?? '',
  beneficiaryType: props.project.beneficiaryType ?? 'main_contact',
  beneficiaryId: props.project.beneficiaryId ?? null,
  beneficiarySnapshot: props.project.beneficiarySnapshot ?? null,
  items: [...(props.project.items ?? [])],
  activity: [...(props.project.activity ?? [])],
  lastSharedAt: props.project.lastSharedAt ?? null,
  lastSharedChannel: props.project.lastSharedChannel ?? null,
});

const localProject = computed(() => ({ ...props.project, ...local }));
const savedProductItems = ref(
  [...(props.project.items ?? [])].map(item => ({ ...item }))
);
const savedActivity = ref(
  [...(props.project.activity ?? [])].map(activity => ({ ...activity }))
);

const showActionsMenu = ref(false);
const showSendChannelPicker = ref(false);
const selectedSendChannel = ref(
  props.contactPreferredChannel === 'email' ? 'email' : 'whatsapp'
);
const composeConversationRef = ref(null);
const productSearchRef = ref(null);
const editDrawerRef = ref(null);

const isShared = computed(() => local.status === 'shared');

const sendChannels = computed(() => [
  {
    value: 'whatsapp',
    icon: 'i-ri-whatsapp-line',
    label: t('PURCHASE_PROJECTS.CHANNELS.WHATSAPP'),
    iconClass: 'bg-[#22c55e1a] text-[#16a34a]',
    borderClass: 'hover:border-[#22c55e80] hover:bg-[#22c55e0d]',
  },
  {
    value: 'email',
    icon: 'i-lucide-mail',
    label: t('PURCHASE_PROJECTS.CHANNELS.EMAIL'),
    iconClass: 'bg-n-blue-3 text-n-blue-11',
    borderClass: 'hover:border-n-blue-4 hover:bg-n-blue-2',
  },
]);

const firstName = computed(() => {
  const parts = props.contactName.trim().split(' ');
  return parts[0] || props.contactName;
});

const buildProductLine = item => {
  const product = mockProducts.find(p => p.id === item.productId);
  if (!product) return '';
  const variant = product.variants.find(v => v.id === item.variantId);
  const price = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: product.currency ?? 'EUR',
  }).format(product.price);
  const mockUrl = `https://boutique.glovely.app/products/${product.sku}`;
  const star = item.isRecommended ? '⭐ ' : '';
  const variantLabel = variant ? ` · ${variant.label}` : '';
  return `${star}${product.title} — ${price}${variantLabel}\n   → ${mockUrl}`;
};

const preparedMessage = computed(() => {
  const intro = t('PURCHASE_PROJECTS.PREVIEW.INTRO');
  const outro = t('PURCHASE_PROJECTS.PREVIEW.OUTRO');
  const signOff = t('PURCHASE_PROJECTS.PREVIEW.SIGN_OFF');
  const productLines = local.items
    .map(buildProductLine)
    .filter(Boolean)
    .join('\n\n');
  return [
    `Bonjour ${firstName.value},`,
    '',
    intro,
    '',
    productLines,
    '',
    outro,
    '',
    signOff,
    props.project.sellerName,
  ].join('\n');
});

const currentProject = () => ({ ...props.project, ...local });

const existingProductIds = () => local.items.map(i => i.productId);

/**
 * Serializes product item drafts for a stable equality check.
 * @param {Array} items Product items currently shown in the project.
 * @returns {string} Stable JSON representation of the product selection.
 */
const serializeProductItems = items => JSON.stringify(items);

/**
 * Stores copies of the product selection and activity last explicitly saved.
 * @param {Array} items Product items to treat as the saved baseline.
 * @param {Array} activity Project activity to treat as the saved baseline.
 * @returns {void}
 */
const rememberSavedProductState = (items, activity) => {
  savedProductItems.value = items.map(item => ({ ...item }));
  savedActivity.value = activity.map(item => ({ ...item }));
};

const hasPendingProductChanges = computed(
  () =>
    serializeProductItems(local.items) !==
    serializeProductItems(savedProductItems.value)
);

/**
 * Adds a local activity entry for product selection changes.
 * @param {string} type Activity type persisted with the project metadata.
 * @param {string} label Translation label for the activity feed.
 * @returns {void}
 */
const addProductActivity = (type, label) => {
  local.activity.unshift({
    type,
    label,
    date: Math.floor(Date.now() / 1000),
  });
};

const updateItem = updatedItem => {
  const idx = local.items.findIndex(
    i =>
      i.productId === updatedItem.productId &&
      i.variantId === updatedItem.variantId
  );
  if (idx !== -1) local.items[idx] = updatedItem;
  addProductActivity(
    'product_updated',
    t('PURCHASE_PROJECTS.ACTIVITY.PRODUCT_UPDATED')
  );
};

const removeItem = removedItem => {
  local.items = local.items.filter(
    i =>
      !(
        i.productId === removedItem.productId &&
        i.variantId === removedItem.variantId
      )
  );
  addProductActivity(
    'product_removed',
    t('PURCHASE_PROJECTS.ACTIVITY.PRODUCT_REMOVED')
  );
};

const addProduct = newItem => {
  local.items.push(newItem);
  addProductActivity(
    'product_added',
    t('PURCHASE_PROJECTS.ACTIVITY.PRODUCT_ADDED')
  );
};

const cancelProductChanges = () => {
  local.items = savedProductItems.value.map(item => ({ ...item }));
  local.activity = savedActivity.value.map(activity => ({ ...activity }));
};

const saveProductChanges = () => {
  const project = currentProject();
  rememberSavedProductState(project.items, project.activity);
  emit('updated', project);
};

const applyProjectUpdate = updates => {
  Object.assign(local, updates);
  local.activity.unshift({
    type: 'project_edited',
    label: t('PURCHASE_PROJECTS.ACTIVITY.PROJECT_EDITED'),
    date: Math.floor(Date.now() / 1000),
  });
  emit('updated', currentProject());
};

const openSendChannelPicker = () => {
  showActionsMenu.value = false;
  selectedSendChannel.value =
    props.contactPreferredChannel === 'email' ? 'email' : 'whatsapp';
  showSendChannelPicker.value = true;
};

const closeSendChannelPicker = () => {
  showSendChannelPicker.value = false;
};

const selectSendChannel = channel => {
  selectedSendChannel.value = channel;
  closeSendChannelPicker();
  composeConversationRef.value?.open();
};

const channelFromInbox = inbox => {
  if (inbox?.channelType === INBOX_TYPES.EMAIL) return 'email';
  if (
    inbox?.channelType === INBOX_TYPES.WHATSAPP ||
    (inbox?.channelType === INBOX_TYPES.TWILIO &&
      inbox?.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP)
  ) {
    return 'whatsapp';
  }
  return selectedSendChannel.value;
};

const channelLabel = channel => {
  const config = sendChannels.value.find(item => item.value === channel);
  return config?.label ?? channel;
};

const markProjectAsShared = ({ inbox } = {}) => {
  const channel = channelFromInbox(inbox);
  const now = Math.floor(Date.now() / 1000);

  local.status = 'shared';
  local.lastSharedAt = now;
  local.lastSharedChannel = channel;
  local.activity.unshift({
    type: 'message_sent',
    label: t('PURCHASE_PROJECTS.ACTIVITY.MESSAGE_SENT_CHANNEL', {
      channel: channelLabel(channel),
    }),
    channel,
    date: now,
  });

  emit('updated', currentProject());
};

const secondaryActions = computed(() => [
  ...(isShared.value
    ? [
        {
          key: 'send_again',
          icon: 'i-lucide-send',
          label: t('PURCHASE_PROJECTS.DETAIL.ACTIONS.SEND_AGAIN'),
          handler: openSendChannelPicker,
        },
      ]
    : []),
  {
    key: 'duplicate',
    icon: 'i-lucide-copy',
    label: t('PURCHASE_PROJECTS.DETAIL.ACTIONS.DUPLICATE'),
    handler: () => {
      showActionsMenu.value = false;
    },
  },
  {
    key: 'archive',
    icon: 'i-lucide-archive',
    label: t('PURCHASE_PROJECTS.DETAIL.ACTIONS.ARCHIVE'),
    handler: () => {
      local.status = 'archived';
      showActionsMenu.value = false;
      emit('updated', currentProject());
    },
  },
]);
</script>

<template>
  <div class="flex flex-col w-full min-h-0">
    <div
      class="flex flex-col px-4 pt-4 pb-3 gap-3 sticky top-0 bg-n-solid-1 z-10"
    >
      <!-- Row 1 : navigation -->
      <div class="flex items-center justify-between">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-chevron-left"
          class="!rounded-full bg-n-alpha-black2 shrink-0"
          @click="emit('back')"
        />
        <div class="relative">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-ellipsis"
            class="!rounded-full bg-n-alpha-black2"
            @click="showActionsMenu = !showActionsMenu"
          />
          <div
            v-if="showActionsMenu"
            class="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-xl border border-n-strong bg-n-solid-1 shadow-lg py-1"
          >
            <button
              v-for="action in secondaryActions"
              :key="action.key"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
              @click="action.handler"
            >
              <span
                :class="action.icon"
                class="size-4 text-n-slate-10 shrink-0"
              />
              {{ action.label }}
            </button>
          </div>
          <div
            v-if="showActionsMenu"
            class="fixed inset-0 z-10"
            @click="showActionsMenu = false"
          />
        </div>
      </div>

      <!-- Row 2 : title + status -->
      <div class="flex items-center gap-2 min-w-0">
        <h2 class="text-lg font-bold text-n-slate-12 truncate">
          {{ local.title }}
        </h2>
        <PurchaseProjectStatusBadge :status="local.status" class="shrink-0" />
      </div>
    </div>

    <div class="bg-n-alpha-1 overflow-y-auto flex flex-col gap-2 py-2">
      <div class="bg-n-solid-1">
        <PurchaseProjectSummary
          section="client"
          :project="localProject"
          :contact-name="contactName"
          :contact-phone="contactPhone"
          :contact-email="contactEmail"
        />
      </div>

      <div class="bg-n-solid-1">
        <PurchaseProjectSummary
          section="project"
          :project="localProject"
          @edit="editDrawerRef?.open()"
        />
      </div>

      <div class="bg-n-solid-1">
        <PurchaseProjectItems
          :items="local.items"
          @update-item="updateItem"
          @remove-item="removeItem"
          @add-product="productSearchRef?.open()"
        />
      </div>

      <div class="bg-n-solid-1">
        <LinkedTasksPreview
          :project="localProject"
          :contact="{
            id: contactId,
            name: contactName,
            phone: contactPhone,
            email: contactEmail,
          }"
          :flush-on-desktop="false"
          :title="t('TASKS.PROJECT_BLOCK_TITLE')"
        />
      </div>

      <div class="bg-n-solid-1">
        <PurchaseProjectActivity :activity="local.activity" />
      </div>
    </div>

    <div
      v-if="hasPendingProductChanges"
      class="sticky bottom-0 flex gap-2 border-t border-n-strong bg-n-solid-1 px-4 py-4"
    >
      <Button
        variant="outline"
        color="slate"
        size="md"
        :label="t('PURCHASE_PROJECTS.DETAIL.CANCEL_CHANGES')"
        class="flex-1"
        data-test-id="purchase-project-cancel-changes"
        @click="cancelProductChanges"
      />
      <Button
        variant="solid"
        color="blue"
        size="md"
        icon="i-lucide-save"
        :label="t('PURCHASE_PROJECTS.DETAIL.SAVE_CHANGES')"
        class="flex-1"
        data-test-id="purchase-project-save-changes"
        @click="saveProductChanges"
      />
    </div>

    <div
      v-else-if="!isShared"
      class="px-4 py-4 border-t border-n-strong sticky bottom-0 bg-n-solid-1"
    >
      <Button
        variant="solid"
        color="blue"
        size="md"
        icon="i-lucide-send"
        :label="t('PURCHASE_PROJECTS.DETAIL.SEND')"
        class="w-full"
        @click="openSendChannelPicker"
      />
    </div>

    <ComposeConversation
      ref="composeConversationRef"
      :contact-id="String(contactId)"
      :initial-message="preparedMessage"
      :initial-channel="selectedSendChannel"
      is-modal
      @conversation-created="markProjectAsShared"
    />

    <Teleport to="body">
      <div
        v-if="showSendChannelPicker"
        class="fixed inset-0 z-[80] flex items-end justify-center bg-n-alpha-black1 backdrop-blur-[4px] sm:items-center sm:px-4"
        @click.self="closeSendChannelPicker"
      >
        <div
          class="flex max-h-[80vh] w-full flex-col overflow-hidden rounded-t-[1.5rem] border border-n-weak bg-n-solid-1 shadow-xl sm:max-w-[27rem] sm:rounded-2xl"
        >
          <div class="flex justify-center pt-2 sm:hidden">
            <span class="h-1 w-10 rounded-full bg-n-slate-6" />
          </div>
          <div class="flex items-center justify-between gap-3 px-4 pb-3 pt-3">
            <h3
              class="min-w-0 truncate text-base font-semibold text-n-slate-12"
            >
              {{ t('PURCHASE_PROJECTS.SEND_CHANNEL.TITLE') }}
            </h3>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-x"
              class="!rounded-full bg-n-alpha-black2 shrink-0"
              @click="closeSendChannelPicker"
            />
          </div>
          <div class="grid grid-cols-2 gap-3 px-4 pb-4">
            <button
              v-for="channel in sendChannels"
              :key="channel.value"
              type="button"
              class="group flex min-h-32 flex-col justify-between rounded-xl border border-n-weak bg-n-alpha-1 p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm"
              :class="channel.borderClass"
              @click="selectSendChannel(channel.value)"
            >
              <span class="flex items-start">
                <span
                  class="flex size-10 items-center justify-center rounded-full"
                  :class="channel.iconClass"
                >
                  <span :class="channel.icon" class="size-5" />
                </span>
              </span>
              <span class="flex min-w-0">
                <span class="truncate text-sm font-semibold text-n-slate-12">
                  {{ channel.label }}
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <ProductSearchDrawer
      ref="productSearchRef"
      :existing-product-ids="existingProductIds()"
      @add-product="addProduct"
    />

    <EditPurchaseProjectDrawer
      ref="editDrawerRef"
      :project="localProject"
      @updated="applyProjectUpdate"
    />
  </div>
</template>
